import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldAlert, Snowflake } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSecurityPulse } from "../hooks/useSecurityPulse";
import { useIdleLock } from "../hooks/useIdleLock";

/**
 * IdleLockGate — Security Pulse v2 · 30-minute inactivity lock.
 *
 * Freezes the entire dashboard after 30 min of no activity for
 * authenticated non-admin users. The scrim covers all interactive
 * routes; auth / verification / privacy pages are exempt so users can
 * still recover their account if the freeze fires on a stale tab.
 *
 * Unlocking fires an `inactivity_unlock` Security Pulse — the caller
 * must re-authenticate with a fresh 6-digit code delivered to their
 * Zoho inbox. The obsidian/gold OTP modal is provided by
 * `useSecurityPulse`, so this component is pure orchestration.
 */
const EXEMPT_PATHS = [
  "/", "/login", "/register", "/register/buyer", "/register/seller",
  "/forgot-password", "/reset-password", "/verify", "/registry",
  "/verification-pending", "/access-suspended", "/privacy",
  "/accessibility", "/leadership", "/terms", "/refund", "/delivery",
];

const IDLE_MS = 30 * 60 * 1000; // 30 minutes

export function IdleLockGate() {
  const { user, api } = useAuth();
  const location = useLocation();
  const pulse = useSecurityPulse(api);

  const role = ((user && typeof user === "object" && user.role) || "").toLowerCase();
  const authed = !!(user && typeof user === "object");
  const exempt = !authed || role === "admin" || role === "super_admin"
                 || EXEMPT_PATHS.includes(location.pathname);

  const lock = useIdleLock({ idleMs: IDLE_MS, enabled: !exempt });

  const requestUnlock = useCallback(async () => {
    try {
      const tok = await pulse.request("inactivity_unlock", null);
      // Any token means Pulse verified — release the freeze.
      if (tok) lock.unfreeze();
    } catch { /* user closed modal — stay frozen */ }
  }, [pulse, lock]);

  // When frozen and pulse modal isn't already open, auto-issue.
  useEffect(() => {
    if (exempt) return;
    if (!lock.frozen) return;
    if (pulse.isOpen) return;
    requestUnlock();
  }, [exempt, lock.frozen, pulse.isOpen, requestUnlock]);

  return (
    <>
      <AnimatePresence>
        {lock.frozen && !exempt && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[75] flex items-center justify-center px-4"
            data-testid="idle-lock-overlay"
            style={{ backdropFilter: "blur(10px)", background: "rgba(5,5,5,0.85)" }}
          >
            <motion.div
              initial={{ y: 12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 8, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-[520px] bg-[#0A0906] border border-[#D8B23E]/40 rounded-sm overflow-hidden"
              style={{ boxShadow: "0 40px 120px -20px rgba(216,178,62,0.35)" }}
            >
              <div className="h-[3px] w-full"
                    style={{ background: "linear-gradient(90deg,#D8B23E,#F6E4A6,#B87333,#F6E4A6,#D8B23E)" }} />
              <div className="p-8 space-y-5">
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center w-11 h-11 rounded-full text-[#050505]"
                          style={{ background: "linear-gradient(135deg,#D8B23E,#F6E4A6,#B87333)",
                                    boxShadow: "0 0 40px rgba(216,178,62,0.4)" }}>
                    <Snowflake size={19} />
                  </span>
                  <div>
                    <div className="font-grotesk text-[10px] uppercase tracking-[0.32em] text-[#8A6A1A]">
                      Vinci Security Pulse v2
                    </div>
                    <div className="font-serif text-2xl text-[#F5F5F0] mt-1 leading-tight">
                      Session Frozen · Inactive 30 min
                    </div>
                  </div>
                </div>

                <p className="font-body text-sm text-[#C9C9C2] leading-relaxed">
                  We paused your desk after <b className="text-[#F5F5F0]">30 minutes</b> of
                  no activity. This is a Vinci compliance safeguard — no
                  quotation, invoice, or trade release can be executed from
                  an unattended terminal.
                </p>

                <div className="rounded-sm border border-[#2a1f04] bg-black/40 p-4">
                  <div className="font-grotesk text-[9px] uppercase tracking-[0.28em] text-[#8A6A1A] mb-2 inline-flex items-center gap-1.5">
                    <ShieldAlert size={11} className="text-[#D8B23E]" />
                    To resume
                  </div>
                  <p className="font-body text-[13px] text-[#C9C9C2] leading-relaxed">
                    Enter a fresh 6-digit Security Pulse. A code has been
                    despatched to your registered email. Codes expire in
                    <b className="text-[#D8B23E]"> 5 minutes</b>.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={requestUnlock}
                  data-testid="idle-lock-resume"
                  className="w-full py-3 rounded-sm bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] text-black font-grotesk text-xs uppercase tracking-[0.28em] hover:brightness-110 transition-all"
                >
                  Re-authorise my Vinci Desk
                </button>

                <div className="text-[10px] text-[#6f6f68] font-body text-center leading-relaxed">
                  Signed in as <b className="text-[#8A8A82]">{user?.email}</b>.
                  Every unlock event is logged to the Vinci Master Ledger
                  with your IP + timestamp.
                </div>
              </div>
              <div className="h-[3px] w-full"
                    style={{ background: "linear-gradient(90deg,#B87333,#F6E4A6,#D8B23E,#F6E4A6,#8A6A1A)" }} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {pulse.modal}
    </>
  );
}
